import React from 'react';
import {
    UploadedDocument,
    WireTransferCategory,
    WireTransferGroup,
} from '../../../models/FlowOfFunds/flow-of-funds.model';
import {
    WIRE_STATUS_BADGE,
    WIRE_STATUS_LABELS,
    formatMoneyExact,
    wireGroupKey,
} from '../../../utils/flowOfFundsFormat';
import { SentConfirmationControl } from './SentConfirmationControl';
import { ReceiptConfirmationControl } from './ReceiptConfirmationControl';
import { EvidenceDocumentSlot } from './EvidenceDocumentSlot';

const CATEGORY_TITLES: Record<WireTransferCategory, string> = {
    [WireTransferCategory.Seller]: 'Seller',
    [WireTransferCategory.ThirdPartyPayoff]: '3rd Party Pay-Off',
    [WireTransferCategory.LegalFees]: 'Legal Fees',
    [WireTransferCategory.VetValueFees]: 'VetValue Fees',
};

export interface WireGroupCardProps {
    group: WireTransferGroup;
    pendingKey: string | null;
    onViewWireInstructions: (group: WireTransferGroup) => void;
    onConfirmSent: (group: WireTransferGroup, paymentDate: Date) => void;
    onUndoConfirmSent: (group: WireTransferGroup) => void;
    onConfirmReceipt: (group: WireTransferGroup) => void;
    onUndoConfirmReceipt: (group: WireTransferGroup) => void;
    onUploadSenderEvidence: (group: WireTransferGroup, file: File) => void;
    onRemoveSenderEvidence: (group: WireTransferGroup) => void;
    onUploadReceiptEvidence: (group: WireTransferGroup, file: File) => void;
    onRemoveReceiptEvidence: (group: WireTransferGroup) => void;
    onViewDocument: (group: WireTransferGroup, document: UploadedDocument) => void;
}

export const WireGroupCard = ({
    group,
    pendingKey,
    onViewWireInstructions,
    onConfirmSent,
    onUndoConfirmSent,
    onConfirmReceipt,
    onUndoConfirmReceipt,
    onUploadSenderEvidence,
    onRemoveSenderEvidence,
    onUploadReceiptEvidence,
    onRemoveReceiptEvidence,
    onViewDocument,
}: WireGroupCardProps) => {
    const key = wireGroupKey(group);
    const isSentPending = pendingKey === `${key}-sent`;
    const isReceiptPending = pendingKey === `${key}-receipt`;

    return (
        <div className="main-card card ff-wires__card">
            <div className="ff-wires__card-head">
                <div className="ff-wires__card-title">
                    <h4>{CATEGORY_TITLES[group.category]}</h4>
                    <span className={`ff-wires__status ${WIRE_STATUS_BADGE[group.status]}`}>
                        {WIRE_STATUS_LABELS[group.status]}
                    </span>
                </div>
                <span className="ff-wires__card-meta">{group.categoryDescription}</span>
            </div>

            <div className="ff-wires__recipient">
                <div>
                    <div className="ff-wires__recipient-name">{group.recipientName}</div>
                    <div className="ff-wires__recipient-sub">{group.recipientDescription}</div>
                </div>
                <div className="ff-wires__recipient-right">
                    <span className="ff-wires__amount">{formatMoneyExact(group.amount)}</span>
                    <button
                        type="button"
                        className="ff-wires__link"
                        disabled={!group.wireInstructions && !group.canEditWireInstructions}
                        onClick={() => onViewWireInstructions(group)}>
                        {group.wireInstructions
                            ? 'View wire instructions'
                            : 'Add wire instructions'}
                    </button>
                </div>
            </div>

            <div className="ff-wires__confirmations">
                <div className="ff-wires__side">
                    <span className="ff-wires__side-label">Sender</span>
                    <div className="ff-wires__side-controls">
                        <SentConfirmationControl
                            group={group}
                            isPending={isSentPending}
                            onConfirm={(paymentDate) => onConfirmSent(group, paymentDate)}
                            onUndo={() => onUndoConfirmSent(group)}
                        />
                        <EvidenceDocumentSlot
                            document={group.senderEvidenceDocument}
                            canUpload={group.canUploadEvidence}
                            canRemove={group.canManageSenderEvidence}
                            uploadLabel="Upload evidence"
                            isUploadPending={pendingKey === `${key}-sender-evidence-upload`}
                            isRemovePending={pendingKey === `${key}-sender-evidence-remove`}
                            isViewPending={pendingKey === `${key}-sender-evidence-view`}
                            inputId={`${key}-sender-evidence-input`}
                            onUpload={(file) => onUploadSenderEvidence(group, file)}
                            onRemove={() => onRemoveSenderEvidence(group)}
                            onView={() => onViewDocument(group, group.senderEvidenceDocument!)}
                        />
                    </div>
                </div>

                <div className="ff-wires__side ff-wires__side--recipient">
                    <span className="ff-wires__side-label">Recipient</span>
                    <div className="ff-wires__side-controls">
                        <ReceiptConfirmationControl
                            group={group}
                            isPending={isReceiptPending}
                            onConfirm={() => onConfirmReceipt(group)}
                            onUndo={() => onUndoConfirmReceipt(group)}
                        />
                        <EvidenceDocumentSlot
                            document={group.receiptEvidenceDocument}
                            canUpload={group.canUploadEvidence}
                            canRemove={group.canManageReceiptEvidence}
                            uploadLabel="Upload evidence"
                            isUploadPending={pendingKey === `${key}-receipt-evidence-upload`}
                            isRemovePending={pendingKey === `${key}-receipt-evidence-remove`}
                            isViewPending={pendingKey === `${key}-receipt-evidence-view`}
                            inputId={`${key}-receipt-evidence-input`}
                            onUpload={(file) => onUploadReceiptEvidence(group, file)}
                            onRemove={() => onRemoveReceiptEvidence(group)}
                            onView={() => onViewDocument(group, group.receiptEvidenceDocument!)}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};
